import type {
  DisputeAction,
  DisputeStatus,
  LineDecision,
  StatementCategory,
} from '@/services/statementVerification';

export const CATEGORY_LABELS: Record<StatementCategory, string> = {
  short_paid: 'Short-paid',
  over_paid: 'Over-paid',
  matched: 'Matched',
  not_in_books: 'In statement, not in our books',
  no_calculation: 'In our books, not calculated',
};

export const CATEGORY_ORDER: StatementCategory[] = [
  'short_paid',
  'over_paid',
  'matched',
  'not_in_books',
  'no_calculation',
];

export const CATEGORY_VARIANT: Record<StatementCategory, 'error' | 'warning' | 'success' | 'info' | 'default'> = {
  short_paid: 'error',
  over_paid: 'warning',
  matched: 'success',
  not_in_books: 'info',
  no_calculation: 'default',
};

export const DISPUTE_LABELS: Record<DisputeStatus, string> = {
  open: 'Open',
  raised: 'Raised with insurer',
  insurer_agreed: 'Insurer agreed',
  insurer_rejected: 'Insurer rejected',
  written_off: 'Written off',
  resolved: 'Resolved',
};

export const DISPUTE_ORDER: DisputeStatus[] = [
  'open',
  'raised',
  'insurer_agreed',
  'insurer_rejected',
  'written_off',
  'resolved',
];

export const ACTION_LABELS: Record<DisputeAction, string> = {
  raise: 'Mark raised with insurer',
  agree: 'Insurer agreed',
  reject: 'Insurer rejected',
  resolve: 'Resolve',
  write_off: 'Write off',
  reopen: 'Reopen',
};

/** Actions that carry an amount (the server requires it for resolve and write-off). */
export const ACTIONS_WITH_AMOUNT: DisputeAction[] = ['agree', 'resolve', 'write_off'];

export const DECISION_LABELS: Record<Exclude<LineDecision, ''>, string> = {
  none: 'No decision',
  accept_statement: 'Accept the statement',
  accept_books: 'Accept our books',
  dispute: 'Dispute',
};

export const FLAG_LABELS: Record<string, string> = {
  premium_diff: 'Premium differs',
  rate_diff: 'Commission % differs',
  rate_unknown: 'Our commission % unknown',
  cancelled_in_statement: 'Cancelled on statement',
  cancelled_in_books: 'Cancelled in our books',
  code_mismatch: 'Intermediary code differs',
  ambiguous_policy: 'Policy number ambiguous',
  prior_period: 'Prior-period rows',
  total_mismatch: 'Statement total does not add up',
};

export const KIND_BADGE: Record<string, string> = {
  endorsement: 'E',
  cancellation: 'C',
};

export function flagLabel(flag: string): string {
  return FLAG_LABELS[flag] ?? flag.replace(/_/g, ' ');
}

/** `2026-09-01` -> `Sep 2026`, without a timezone shift. */
export function periodLabel(period: string): string {
  const [y, m] = period.split('-').map(Number);
  if (!y || !m) return period;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-IN', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}
